<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { NButton, NEmpty, useMessage } from 'naive-ui'
import type { MaterialGoods, MaterialResource } from '@/material/web/api'
import { applyDollCompositeToWorkspace } from '@/features/material-library/apply'
import { isDollCompositeAlreadyApplied, resolveMaterialUsageRoot } from '@/features/material-library/usage'
import { getMaterialPreviewUrl } from '@/material/web/api'
import MaterialPreviewLightbox from '@/material/web/MaterialPreviewLightbox.vue'
import MaterialPreviewSizeSelect from '@/material/web/MaterialPreviewSizeSelect.vue'
import { useWorkspaceStore } from '@/store/workspace'
import {
  buildDollBundle,
  buildMaterialResourceStableKey,
  extractPartPreviewId,
  filterDollPartPreviewOptions,
  filterResolvableSchemeThumbs,
  getDollLayerUrls,
  getDollSelectedResources,
  getFlatAssetThumbs,
  getResourceExtra,
  isBackgroundCgCompositeCategory,
  resolveDollBodyResourceFromThumb,
  resolveMatchedMaterialSize,
  resolveMaterialPreviewSelectedSize,
  resolvePreferredMaterialSize,
  buildMaterialSizeOptions,
  collectDollCompositeResolvableSizes,
} from '@/material/web/previewUtils'

const props = defineProps<{
  goods: MaterialGoods
  resources: MaterialResource[]
  topCategoryId: string
  collected: boolean
  previewLoading?: boolean
}>()

const emit = defineEmits<{
  toggleCollect: []
}>()

const selectedSize = ref('')
const selectedPartKey = ref('')
const selectedBodyExtra = ref('')
const selectedSchemeThumbId = ref('')
const selectedPartIds = ref<Record<string, string>>({})
const lightboxOpen = ref(false)
const applying = ref(false)
const message = useMessage()
const workspace = useWorkspaceStore()

const isHorizontalComposite = computed(() => isBackgroundCgCompositeCategory(props.topCategoryId))
const dollBundle = computed(() => buildDollBundle(props.resources, props.topCategoryId))
const schemeThumbEntries = computed(() => {
  if (!isHorizontalComposite.value) return []
  const thumbs = filterResolvableSchemeThumbs(
    props.resources,
    dollBundle.value,
    props.topCategoryId,
    getFlatAssetThumbs(props.resources),
  )
  return thumbs.map((item, index) => ({
    item,
    key: buildMaterialResourceStableKey(item, index),
  }))
})
const sizeOptions = computed(() =>
  buildMaterialSizeOptions(
    collectDollCompositeResolvableSizes(
      dollBundle.value,
      selectedBody.value,
      selectedPartIds.value,
    ),
    props.topCategoryId,
  ),
)
const sizeValues = computed(() => sizeOptions.value.map((item) => item.value))
const preferredSize = computed(() => {
  const sizes = sizeValues.value
  const width = workspace.project?.screenHash.width ?? workspace.project?.Width ?? 0
  const height = workspace.project?.screenHash.height ?? workspace.project?.Height ?? 0
  if (isHorizontalComposite.value) {
    return resolveMatchedMaterialSize(sizes, width, height)
  }
  return resolvePreferredMaterialSize(sizes, width, height)
})
const selectedBody = computed(
  () =>
    dollBundle.value.bodies.find((item) => getResourceExtra(item) === selectedBodyExtra.value) ??
    dollBundle.value.bodies[0] ??
    null,
)
const currentPartOptions = computed(() =>
  filterDollPartPreviewOptions(dollBundle.value, selectedPartKey.value, sizeValues.value),
)
const previewDisplaySize = computed(() => applySize.value)
const previewLayers = computed(() => {
  const size = previewDisplaySize.value
  if (!size) return []
  return getDollLayerUrls(dollBundle.value, selectedBody.value, selectedPartIds.value, size)
})
const applySize = computed(() => {
  const sizes = sizeValues.value
  if (selectedSize.value && sizes.includes(selectedSize.value)) {
    return selectedSize.value
  }
  if (preferredSize.value && sizes.includes(preferredSize.value)) {
    return preferredSize.value
  }
  return sizes[0] ?? ''
})
const selectedApplyResources = computed(() => {
  const size = applySize.value
  if (!size) return []
  return getDollSelectedResources(dollBundle.value, selectedBody.value, selectedPartIds.value, size)
})
const alreadyApplied = computed(() => {
  const size = applySize.value
  if (!size) return false
  const root = resolveMaterialUsageRoot(props.topCategoryId, workspace.virtualResourceRoots)
  return isDollCompositeAlreadyApplied(root, {
    goodsId: props.goods.id,
    size,
    resources: selectedApplyResources.value,
  })
})

function resetBodySelection(bundle: ReturnType<typeof buildDollBundle>) {
  if (isHorizontalComposite.value) {
    const firstEntry = schemeThumbEntries.value[0] ?? null
    selectedSchemeThumbId.value = firstEntry?.key ?? ''
    const body = resolveDollBodyResourceFromThumb(props.resources, firstEntry?.item ?? null)
    selectedBodyExtra.value = body ? getResourceExtra(body) : getResourceExtra(bundle.bodies[0])
    return
  }
  selectedSchemeThumbId.value = ''
  selectedBodyExtra.value = getResourceExtra(bundle.bodies[0])
}

watch(
  dollBundle,
  (bundle) => {
    selectedPartKey.value = bundle.partKeys[0] ?? ''
    selectedPartIds.value = {}
    resetBodySelection(bundle)
  },
  { immediate: true },
)

watch(
  [sizeValues, preferredSize, () => props.goods.id, sizeOptions, selectedPartIds, selectedBodyExtra],
  () => {
    selectedSize.value = resolveMaterialPreviewSelectedSize(
      sizeValues.value,
      preferredSize.value,
      selectedSize.value,
    )
  },
  { immediate: true },
)

function selectSchemeThumb(key: string) {
  selectedSchemeThumbId.value = key
  const thumb = schemeThumbEntries.value.find((entry) => entry.key === key)?.item ?? null
  const body = resolveDollBodyResourceFromThumb(props.resources, thumb)
  if (body) {
    selectedBodyExtra.value = getResourceExtra(body)
  }
}

function updateSelectedPart(resource?: MaterialResource) {
  if (!selectedPartKey.value) return
  const next = { ...selectedPartIds.value }
  next[selectedPartKey.value] = resource ? extractPartPreviewId(resource) : ''
  selectedPartIds.value = next
}

async function applyCurrentSelection() {
  if (!applySize.value) {
    message.warning('当前工程分辨率与素材可用档位不匹配，无法应用')
    return
  }
  if (!selectedApplyResources.value.length) {
    message.warning('当前没有可应用的素材资源')
    return
  }
  applying.value = true
  try {
    await applyDollCompositeToWorkspace({
      topCategoryId: props.topCategoryId,
      goods: props.goods,
      resources: selectedApplyResources.value,
      size: applySize.value,
    })
  } catch (error) {
    message.error(error instanceof Error ? error.message : '素材应用失败')
  } finally {
    applying.value = false
  }
}
</script>

<template>
  <div class="preview-pane" :class="{ 'preview-pane-horizontal': isHorizontalComposite }">
    <header class="preview-header">
      <div class="preview-title">{{ goods.name }}</div>
      <div class="preview-subtitle">{{ goods.extra || goods.intro || '暂无说明' }}</div>
      <div class="preview-actions">
        <n-button size="small" @click="emit('toggleCollect')">
          {{ collected ? '取消收藏' : '加入收藏' }}
        </n-button>
        <n-button
          size="small"
          type="primary"
          secondary
          :loading="applying"
          :disabled="!selectedApplyResources.length || alreadyApplied"
          @click="applyCurrentSelection"
        >
          {{ alreadyApplied ? '已使用' : '应用' }}
        </n-button>
        <MaterialPreviewSizeSelect v-model="selectedSize" :options="sizeOptions" />
        <n-button size="small" type="primary" :disabled="!previewLayers.length" @click="lightboxOpen = true">
          预览全图
        </n-button>
      </div>
    </header>

    <div v-if="previewLayers.length && !previewLoading" class="preview-stage">
      <div class="preview-canvas">
        <img
          v-for="(url, index) in previewLayers"
          :key="`${url}-${index}`"
          :src="url"
          alt=""
          class="preview-layer"
          :class="{ 'preview-layer-overlay': index > 0 }"
        />
      </div>
    </div>
    <n-empty v-else-if="previewLoading" description="正在加载预览…" class="preview-empty" />
    <n-empty v-else description="当前素材没有可预览图片" class="preview-empty" />

    <div v-if="dollBundle.partKeys.length" class="part-tabs">
      <button
        v-for="key in dollBundle.partKeys"
        :key="key"
        type="button"
        class="part-tab"
        :class="{ 'part-tab-active': selectedPartKey === key }"
        @click="selectedPartKey = key"
      >
        {{ key }}
      </button>
    </div>

    <div class="option-layout">
      <div v-if="currentPartOptions.length" class="part-list">
        <button
          type="button"
          class="part-item part-item-none"
          :class="{ 'part-item-active': !selectedPartIds[selectedPartKey] }"
          @click="updateSelectedPart()"
        >
          无
        </button>
        <button
          v-for="item in currentPartOptions"
          :key="item.id"
          type="button"
          class="part-item"
          :class="{ 'part-item-active': selectedPartIds[selectedPartKey] === extractPartPreviewId(item) }"
          @click="updateSelectedPart(item)"
        >
          <img :src="getMaterialPreviewUrl(item.resmd5)" :alt="item.extra" class="part-thumb" />
        </button>
      </div>

      <div v-if="schemeThumbEntries.length" class="scheme-grid">
        <button
          v-for="{ item, key } in schemeThumbEntries"
          :key="key"
          type="button"
          class="scheme-item"
          :class="{ 'scheme-item-active': selectedSchemeThumbId === key }"
          @click="selectSchemeThumb(key)"
        >
          <img :src="getMaterialPreviewUrl(item.resmd5)" :alt="item.extra" class="scheme-thumb" />
        </button>
      </div>
      <div v-else-if="dollBundle.bodies.length" class="body-list">
        <button
          v-for="item in dollBundle.bodies"
          :key="item.id"
          type="button"
          class="body-item"
          :class="{ 'body-item-active': selectedBodyExtra === getResourceExtra(item) }"
          @click="selectedBodyExtra = getResourceExtra(item)"
        >
          <img :src="getMaterialPreviewUrl(item.resmd5)" :alt="item.extra" class="body-thumb" />
        </button>
      </div>
    </div>

    <MaterialPreviewLightbox
      v-model:show="lightboxOpen"
      v-model:selectedSize="selectedSize"
      :title="goods.name"
      caption=""
      :size-options="sizeOptions"
      :layer-urls="previewLayers"
    />
  </div>
</template>

<style scoped>
.preview-pane {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 100%;
  overflow: visible;
  padding: 8px;
  box-sizing: border-box;
  background: var(--theme-bg-panel);
  font-family: var(--material-font-family, "Microsoft YaHei", "PingFang SC", Arial, sans-serif);
  font-size: var(--material-font-size-base, 12px);
  line-height: var(--material-line-height, 1.35);
  color: var(--theme-text-primary);
}

.preview-header {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
}

.preview-title {
  font-size: var(--material-font-size-title, 13px);
  font-weight: 600;
  color: var(--theme-text-heading);
}

.preview-subtitle,
.preview-caption {
  font-size: var(--material-font-size-small, 11px);
  color: var(--theme-text-secondary);
  line-height: var(--material-line-height, 1.35);
  word-break: break-word;
}

.preview-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
}

.preview-stage {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
  padding: 6px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-raised);
}

.preview-canvas {
  position: relative;
  width: 100%;
  height: 382px;
  background: var(--theme-bg-secondary);
}

.preview-pane-horizontal .preview-canvas {
  height: 220px;
}

.preview-pane-horizontal .preview-stage {
  width: 100%;
}

.preview-pane-horizontal .preview-layer {
  object-fit: cover;
}

.preview-layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.preview-layer-overlay {
  pointer-events: none;
}

.part-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  flex-shrink: 0;
}

.part-tab {
  padding: 4px 8px;
  border: 1px solid var(--theme-border);
  background: transparent;
  color: var(--theme-text-primary);
  cursor: pointer;
  font-size: var(--material-font-size-base, 12px);
  line-height: var(--material-line-height, 1.35);
}

.part-tab-active {
  border-color: var(--theme-accent);
  background: var(--theme-bg-selected);
  color: var(--theme-text-heading);
}

.option-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 32px;
  gap: 6px;
  flex-shrink: 0;
}

.part-list {
  display: grid;
  grid-template-columns: repeat(2, 105px);
  grid-auto-rows: 37px;
  gap: 4px;
  overflow: visible;
  align-content: start;
}

.part-item,
.body-item {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
  cursor: pointer;
}

.part-item-active,
.body-item-active {
  border-color: var(--theme-accent);
  background: var(--theme-bg-selected);
}

.part-item-none {
  color: var(--theme-text-secondary);
}

.part-thumb {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.body-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: visible;
}

.body-thumb {
  display: block;
  width: 24px;
  height: auto;
  object-fit: cover;
}

.scheme-grid {
  display: grid;
  grid-template-columns: 250px;
  grid-auto-rows: 134px;
  gap: 6px;
  overflow: visible;
  align-content: start;
}

.scheme-item {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
  cursor: pointer;
  overflow: hidden;
}

.scheme-item-active {
  border-color: var(--theme-accent);
  background: var(--theme-bg-selected);
}

.scheme-thumb {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-pane-horizontal .option-layout {
  grid-template-columns: minmax(0, 1fr);
}

.preview-empty {
  flex-shrink: 0;
}

</style>
